import { ComponentChildren, RefCallback } from 'preact';
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  useCallback,
  useContext,
} from 'preact/compat';
import Visualizer from '@libertify.frontend/visualizer/lib/Visualizer';
import useCurrentBreakpoint from 'src/hooks/useCurrentBreakpoint';
import { globalContext } from 'src/globalContext';
import { DEFAULT_WIDGET_STYLE } from 'src/constants';
interface AvatarProps {
  mediaSrc: string;
  type?: string;
  mediaStyle: CSSProperties;
  videoThumbnailUrl?: string;
}
type AvatarComponentProps = {
  audioLink: string;
};

const AvatarComponent = forwardRef(function AvatarComponent(
  { audioLink }: AvatarComponentProps,
  myRef,
) {
  const { config } = useContext(globalContext);
  const widgetStyles = config?.styles?.widget;
  const currentBreakpoint = useCurrentBreakpoint(config);
  const breakpointConfig = widgetStyles?.[currentBreakpoint];
  let width, height;
  if (breakpointConfig?.width && breakpointConfig?.height) {
    ({ width, height } = breakpointConfig);
  }
  // Search each lower breakpoint if the larger one isn't defined, and fallback to the default if none are defined
  if (!width || !height) {
    ({ width, height } = widgetStyles?.['medium'] ??
      widgetStyles?.['small'] ?? {
        width: widgetStyles?.width ?? DEFAULT_WIDGET_STYLE.width,
        height: widgetStyles?.height ?? DEFAULT_WIDGET_STYLE.height,
      });
  }

  return (
    <Visualizer
      ref={(ref) => {
        (
          myRef as RefCallback<{
            play: () => void;
            pause: () => void;
          }>
        )(ref);
      }}
      width={width.replace('px', '')}
      height={height.replace('px', '')}
      audioLink={audioLink}
    />
  );
});

type Ref = {
  media?: HTMLAudioElement | HTMLVideoElement | null;
  visualizer?: {
    play: () => void;
    pause: () => void;
  };
};

// New StaticImage component
interface StaticImageProps {
  mediaStyle: CSSProperties;
}

const StaticImage = ({ mediaStyle }: StaticImageProps) => {
  const { config } = useContext(globalContext);
  let staticImageUrl = config?.static_image_url;

  if (!staticImageUrl) return null;

  if (!staticImageUrl?.startsWith('https://')) {
    staticImageUrl = `${config?.widget_base}/data/organizations/${config?.organization_id}/images/${staticImageUrl}`;
  }
  if (
    !staticImageUrl?.startsWith('https://') &&
    !staticImageUrl.startsWith('http://localhost')
  )
    return null;

  return (
    <img
      src={staticImageUrl}
      alt="Avatar"
      style={{
        ...mediaStyle,
        objectFit: 'cover',
      }}
    />
  );
};

const MediaElement = forwardRef(function MediaElement(
  {
    children,
    mediaStyle,
    type,
    videoThumbnailUrl,
    ...props
  }: Readonly<
    {
      type: string;
      children: ComponentChildren;
      src: string;
      videoThumbnailUrl?: string;
    } & Omit<AvatarProps, 'mediaSrc'>
  >,
  avatarRef: ForwardedRef<Ref>,
) {
  // Check if source is an MP3 file
  const config = useContext(globalContext).config;

  // Memoize the ref callbacks to not re-create them on every render
  // without this, the ref callbacks are recreated on every render
  // and were calling the parent avatarRefCallback on every render
  const handleVisualizerRef = useCallback(
    (ref) => {
      if (!ref) return;
      (avatarRef as (ref: Ref) => void)({
        visualizer: ref,
      });
    },
    [avatarRef],
  );

  const handleMediaRef = useCallback(
    (ref) => {
      if (!ref) return;
      (avatarRef as (ref: Ref) => void)({
        media: ref,
      });
    },
    [avatarRef],
  );

  if (type === 'visualizer') {
    return (
      <div>
        <AvatarComponent audioLink={props.src} ref={handleVisualizerRef} />
      </div>
    );
  } else if (type === 'video') {
    return (
      <div
        style={{
          overflow: 'hidden',
          borderRadius: '50%',
          position: 'relative',
          width: mediaStyle?.width || '100%',
          height: mediaStyle?.height || '100%',
          backgroundColor: config?.static_image_url ? 'white' : undefined,
        }}
      >
        {config?.static_image_url ? (
          <>
            <StaticImage mediaStyle={mediaStyle} />
            <audio
              {...props}
              style={{ display: 'none' }}
              ref={handleMediaRef}
            />
          </>
        ) : (
          <video
            playsInline
            style={mediaStyle}
            ref={handleMediaRef}
            poster={videoThumbnailUrl}
            {...props}
          >
            {children}
            <track kind="captions">Not implemented yet</track>
          </video>
        )}
      </div>
    );
  } else if (type === 'audio') {
    // Audio with static image display
    return (
      <div
        style={{
          overflow: 'hidden',
          borderRadius: '50%',
          position: 'relative',
          width: mediaStyle?.width || '100%',
          height: mediaStyle?.height || '100%',
        }}
      >
        <StaticImage mediaStyle={mediaStyle} />
        <audio
          style={{ position: 'absolute', opacity: 0 }}
          ref={handleMediaRef}
          {...props}
        >
          {children}
        </audio>
      </div>
    );
  } else throw new Error('Invalid type');
});

export default forwardRef(function Avatar(
  {
    mediaSrc,
    type = 'video',
    mediaStyle,
    videoThumbnailUrl,
  }: Readonly<AvatarProps>,
  avatarRef: ForwardedRef<Ref>,
) {
  // Determine if we need to use audio type based on file extension or mime type
  let mediaType = type;
  if (mediaSrc.endsWith('.mp3') || mediaSrc.includes('audio/')) {
    mediaType = 'audio';
  }
  return (
    <MediaElement
      type={mediaType}
      ref={avatarRef}
      mediaStyle={{
        width: '100%' /* Scale the video to fit the container */,
        height: 'auto',
        position: 'absolute',
        top: 0,
        ...mediaStyle,
      }}
      src={mediaSrc}
      videoThumbnailUrl={videoThumbnailUrl}
    >
      {mediaType === 'audio' ? (
        <source id="libertify-audio" src={mediaSrc} type="audio/mpeg" />
      ) : (
        <source id="libertify-video" src={mediaSrc} type="video/mp4" />
      )}
      Your browser does not support the {mediaType} tag.
    </MediaElement>
  );
});
